// src/components/ecommerce/FeaturedSection.jsx
import React, { useState, useRef } from 'react';
import { 
  Star,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  ShoppingCart,
  Heart,
  Eye,
  Zap,
  TrendingUp,
  Crown,
  Gift,
  Package,
  Timer,
  Percent
} from 'lucide-react';
import { ProductCard } from './ProductCard';

export const FeaturedSection = ({ 
  products = [],
  onProductClick,
  onAddToCart,
  styling = {},
  title = "Featured Products",
  subtitle = "Discover our hand-picked favorites",
  layout = 'grid', // grid, carousel, masonry
  showViewAll = true,
  maxProducts = 8,
  className = ''
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const scrollContainerRef = useRef(null);

  const displayProducts = products.slice(0, maxProducts);
  const productsPerPage = layout === 'carousel' ? 4 : maxProducts;
  const totalPages = Math.ceil(displayProducts.length / productsPerPage);

  const scrollLeft = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  const nextPage = () => {
    setCurrentPage(prev => (prev + 1) % totalPages);
  };

  const prevPage = () => {
    setCurrentPage(prev => (prev - 1 + totalPages) % totalPages);
  };

  if (!displayProducts.length) {
    return null;
  }

  const FeaturedBadge = ({ product }) => {
    if (product.badge) {
      return (
        <div className="absolute top-2 left-2 z-10">
          <span className={`px-2 py-1 text-xs font-bold rounded-full ${
            product.badge === 'bestseller' ? 'bg-yellow-500 text-white' :
            product.badge === 'new' ? 'bg-green-500 text-white' :
            product.badge === 'sale' ? 'bg-red-500 text-white' :
            'bg-blue-500 text-white'
          }`}>
            {product.badge === 'bestseller' && <Crown className="inline h-3 w-3 mr-1" />}
            {product.badge === 'new' && <Zap className="inline h-3 w-3 mr-1" />}
            {product.badge === 'sale' && <Percent className="inline h-3 w-3 mr-1" />}
            {product.badge.toUpperCase()}
          </span>
        </div>
      );
    }
    return null;
  };

  return (
    <section className={`py-16 bg-gray-50 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm font-medium text-gray-700 mb-4 shadow-sm">
            <Star className="h-4 w-4 text-yellow-500 fill-current" />
            Featured Collection
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        {/* Products Display */}
        {layout === 'carousel' ? (
          <div className="relative">
            {/* Carousel Container */}
            <div 
              ref={scrollContainerRef}
              className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {displayProducts.map((product, index) => (
                <div key={product.id} className="flex-shrink-0 w-72">
                  <div className="relative">
                    <FeaturedBadge product={product} />
                    <ProductCard
                      product={product}
                      onClick={() => onProductClick?.(product)}
                      onAddToCart={onAddToCart}
                      styling={styling}
                      className="h-full"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Carousel Controls */}
            <button
              onClick={scrollLeft}
              className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 z-10"
            >
              <ChevronLeft className="h-5 w-5 text-gray-600" />
            </button>
            
            <button
              onClick={scrollRight}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 z-10"
            >
              <ChevronRight className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        ) : layout === 'masonry' ? (
          <div className="columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-6">
            {displayProducts.map((product, index) => (
              <div key={product.id} className="break-inside-avoid mb-6">
                <div className="relative">
                  <FeaturedBadge product={product} />
                  <ProductCard
                    product={product}
                    onClick={() => onProductClick?.(product)}
                    onAddToCart={onAddToCart}
                    styling={styling}
                    viewMode="masonry"
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Grid Layout
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {displayProducts.map((product, index) => (
              <div key={product.id} className="relative">
                <FeaturedBadge product={product} />
                <ProductCard
                  product={product}
                  onClick={() => onProductClick?.(product)}
                  onAddToCart={onAddToCart}
                  styling={styling}
                />
              </div>
            ))}
          </div>
        )}

        {/* View All Button */}
        {showViewAll && (
          <div className="text-center mt-12">
            <button
              onClick={() => onProductClick?.('view-all')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-gray-900 rounded-lg font-semibold text-lg border-2 border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all duration-200"
            >
              <Package className="h-5 w-5" />
              View All Products
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Featured Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 p-8 bg-white rounded-2xl shadow-sm">
          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mx-auto mb-3">
              <TrendingUp className="h-6 w-6 text-blue-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">
              {displayProducts.filter(p => p.badge === 'bestseller').length}
            </div>
            <div className="text-sm text-gray-600">Bestsellers</div>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg mx-auto mb-3">
              <Zap className="h-6 w-6 text-green-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">
              {displayProducts.filter(p => p.badge === 'new').length}
            </div>
            <div className="text-sm text-gray-600">New Arrivals</div>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-lg mx-auto mb-3">
              <Percent className="h-6 w-6 text-red-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">
              {displayProducts.filter(p => p.salePrice && p.salePrice < p.price).length}
            </div>
            <div className="text-sm text-gray-600">On Sale</div>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-lg mx-auto mb-3">
              <Star className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">
              {Math.round(displayProducts.reduce((acc, p) => acc + (p.rating || 0), 0) / displayProducts.length * 10) / 10}
            </div>
            <div className="text-sm text-gray-600">Avg Rating</div>
          </div>
        </div>
      </div>
    </section>
  );
};